/**
 * Where each feature module appears inside a project.
 *
 * PST is project-centred, as established planning tools are: you open a
 * project and find its areas as tabs. Each module that has a user interface
 * contributes one tab here. Which tabs appear is decided by the server's list
 * of ACTIVE modules (ADR 0002, rule 3); a module switched off has no tab.
 *
 * Modules 2 to 7 will each add an entry - costs, bills of quantities, time
 * recording - instead of crowding the main navigation.
 */
export interface ModuleView {
  /** Module key, as in the server's manifest. */
  module: string;
  /** Path segment under /projekte/:id/. */
  path: string;
  label: string;
  /** Used now and then: listed under "Mehr" instead of as a tab of its own. */
  more?: boolean;
  /** Tabs of one group stand together; a thin line separates the groups. */
  group?: 'planung' | 'kosten' | 'projekt';
}

export const MODULE_VIEWS: readonly ModuleView[] = [
  // Order of use: the plan first (a project opens there), money next, then
  // the hours booked every day and the project's master data.
  { module: 'scheduling', path: 'termine', label: 'Terminplan', group: 'planung' },
  { module: 'scheduling', path: 'aufgaben', label: 'Aufgaben', group: 'planung' },
  { module: 'scheduling', path: 'kalender', label: 'Kalender', group: 'planung' },
  { module: 'costs', path: 'kosten', label: 'Kosten', group: 'kosten' },
  { module: 'tendering', path: 'ausschreibung', label: 'Ausschreibung', group: 'kosten' },
  { module: 'tendering', path: 'vergabe', label: 'Vergabe', group: 'kosten' },
  { module: 'projectdata', path: 'zeiten', label: 'Zeiten', group: 'projekt' },
  { module: 'projectdata', path: 'projekt', label: 'Projektdaten', group: 'projekt' },
  { module: 'projectdata', path: 'dateien', label: 'Dateien', more: true },
];

/** Tabs the core itself contributes to every project, after the modules'. */
export const CORE_VIEWS: readonly { path: string; label: string; more?: boolean; group?: ModuleView['group'] }[] = [
  { path: 'team', label: 'Team', more: true },
  { path: 'snapshots', label: 'Snapshots', more: true },
];

/**
 * The office's reference libraries under Verwaltung › Bibliotheken, one tab
 * each. A module that brings a library adds an entry here and its component
 * in LibrariesPage; the tab shows when the user holds `permission`.
 */
export interface LibraryView {
  module: string;
  /** Path segment under /bibliotheken/. */
  path: string;
  label: string;
  description: string;
  permission: string;
}

export const LIBRARY_VIEWS: readonly LibraryView[] = [
  { module: 'costs', path: 'kennwerte', label: 'Kosten-Kennwerte', description: 'Kennwertkatalog für die Kostenplanung, z. B. aus BKI', permission: 'costs.read' },
  { module: 'costs', path: 'din276', label: 'DIN 276', description: 'Kostengruppen mit den Anmerkungen der Norm, Quelle der Gliederung im Kostenplan', permission: 'costs.read' },
  { module: 'costs', path: 'din277', label: 'DIN 277', description: 'Grundflächen und Rauminhalte: Begriffe, Gliederung der Flächen, Ermittlungsregeln', permission: 'costs.read' },
  { module: 'tendering', path: 'ausschreibungstexte', label: 'Ausschreibungstexte', description: 'Textbibliotheken für Leistungsverzeichnisse: Bürotexte, Herstellertexte', permission: 'tendering.read' },
  { module: 'core', path: 'eingang', label: 'Eingangsordner', description: 'Dateien aus einem Ordner automatisch einlesen, Quittungen im Ausgang und per Mail', permission: 'users.manage' },
  { module: 'tendering', path: 'protokoll', label: 'Einleseprotokoll', description: 'jedes Einlesen von GAEB-Dateien, von Hand und aus dem Eingangsordner', permission: 'users.manage' },
];
